import * as Sentry from '@sentry/react';
import { collectLayoutRenderableFieldQids } from './collectLayoutRenderableFieldQids';
import type { LayoutNode } from './types';

const REPORTED_KEY = 'roger:bespoke-hidden-qids-reported';
const reportedQids = new Set<string>(readReported());

/** Qids already reported this tab session; empty when storage is unavailable. */
function readReported(): string[] {
  try {
    const parsed: unknown = JSON.parse(
      sessionStorage.getItem(REPORTED_KEY) ?? '[]'
    );
    return Array.isArray(parsed)
      ? parsed.filter(q => typeof q === 'string')
      : [];
  } catch {
    return [];
  }
}

/** Field qids under `nodes` that the form does not ask. */
export function unservedFieldQids(
  nodes: LayoutNode[],
  schemaMap: ReadonlyMap<string, unknown>
): string[] {
  return [...collectLayoutRenderableFieldQids(nodes)].filter(
    qid => !schemaMap.has(qid)
  );
}

// Content that shows something without being a field of its own: an option of
// a parent multiselect, or an answer read from another document. A total is
// not: over unasked questions it would show a misleading 0.
const KEPT_CONTENT_TYPES = new Set([
  'OASISMultiselectOption',
  'external-answer',
]);

function holdsKeptContent(value: unknown): boolean {
  if (Array.isArray(value)) return value.some(holdsKeptContent);
  if (value === null || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  if (typeof record.type === 'string' && KEPT_CONTENT_TYPES.has(record.type)) {
    return true;
  }
  // A multiselect's own qid decides whether its options show.
  if (record.type === 'OASISMultiselect') return false;
  return Object.values(record).some(holdsKeptContent);
}

/**
 * True when `nodes` hold fields, the form asks none of them, and nothing else
 * in them shows content; print applies the same rule.
 */
export function holdsOnlyUnservedFields(
  nodes: LayoutNode[],
  schemaMap: ReadonlyMap<string, unknown>
): boolean {
  const qids = collectLayoutRenderableFieldQids(nodes);
  return (
    qids.size > 0 &&
    [...qids].every(qid => !schemaMap.has(qid)) &&
    !holdsKeptContent(nodes)
  );
}

/**
 * Report hidden fields once per qid per tab session, grouped as one Sentry
 * issue: placing a question before the form asks it is normal, but a
 * mistyped qid should still be noticed.
 */
export function reportHiddenFields(qids: string[]): void {
  const fresh = qids.filter(qid => !reportedQids.has(qid));
  if (fresh.length === 0) return;
  fresh.forEach(qid => reportedQids.add(qid));
  try {
    sessionStorage.setItem(REPORTED_KEY, JSON.stringify([...reportedQids]));
  } catch {
    // Without storage the in-memory set still dedupes within this page load.
  }
  Sentry.withScope(scope => {
    scope.setFingerprint(['bespoke-field-missing-from-schema']);
    scope.setExtra('qids', fresh);
    Sentry.captureMessage(
      'Bespoke layout fields hidden: the form does not ask them',
      'warning'
    );
  });
}
